import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { playSfxByTag } from "../../lib/soundBus";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import ItemCard from "./ItemCard";
import { toast } from "sonner";
import { MessageCircle, Coins, Swords, Hand, Store } from "lucide-react";

export default function NpcInteractModal({ open, onOpenChange, gameId, npcToken, character, stats, onCharacterUpdate, onAttack }) {
  const [amount, setAmount] = useState(1);
  const [currency, setCurrency] = useState("gp");
  const [lineIndex, setLineIndex] = useState(0);
  const lines = useMemo(() => npcToken?.dialogue_lines?.filter((l) => l.trim()) || [], [npcToken]);
  const currentLine = useMemo(() => lines[lineIndex % (lines.length || 1)], [lines, lineIndex]);

  // A little coin jingle whenever a shopkeeper's window opens, so players get an audible cue
  // they're somewhere they can spend money (separate from the plain dialogue chime).
  useEffect(() => {
    if (open && npcToken?.is_shopkeeper) playSfxByTag(gameId, "shop_coin_jingle");
  }, [open, npcToken?.id, npcToken?.is_shopkeeper, gameId]);

  if (!npcToken) return null;

  const reroll = () => setLineIndex((i) => i + 1);

  const giveMoney = async () => {
    if (!character) return toast.error("You need a character to give money");
    try {
      const { data } = await api.post(`/characters/${character.id}/give-money`, {
        game_id: gameId, amount, currency, npc_name: npcToken.name,
      });
      onCharacterUpdate?.(data.character);
      toast.success(`Gave ${amount} ${currency} to ${npcToken.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not give money");
    }
  };

  const attack = () => {
    onOpenChange(false);
    onAttack?.(npcToken);
  };

  const pickpocket = async () => {
    if (!character || !stats?.skills?.thievery) return toast.error("You need a character with a Thievery skill to try this");
    try {
      const { data } = await api.post(`/characters/${character.id}/pickpocket`, { game_id: gameId, token_id: npcToken.id });
      if (data.success) toast.success(data.message);
      else toast.error(data.message);
      onOpenChange(false);
    } catch {
      toast.error("Could not attempt pickpocket");
    }
  };

  const buyItem = async (idx, item) => {
    if (!character) return toast.error("You need a character to buy items");
    try {
      const { data } = await api.post(`/characters/${character.id}/buy-item`, { game_id: gameId, token_id: npcToken.id, item_index: idx });
      onCharacterUpdate?.(data.character);
      toast.success(`Bought ${item.name} for ${item.price_amount || 0} ${item.price_currency || "gp"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not buy that item");
    }
  };

  const sellItem = async (idx, item) => {
    if (!character) return toast.error("You need a character to sell items");
    try {
      const { data } = await api.post(`/characters/${character.id}/sell-item`, { game_id: gameId, token_id: npcToken.id, item_index: idx });
      onCharacterUpdate?.(data.character);
      toast.success(`Sold ${item.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not sell that item");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#322216] border-white/10 text-[#f1e0c5] max-w-sm" data-testid="npc-interact-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-[#D4AF37]" /> {npcToken.name}
          </DialogTitle>
          <DialogDescription>You walk up to {npcToken.name}.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-1">
          {lines.length > 0 ? (
            <div className="bg-black/30 border border-white/10 rounded-xl p-3 space-y-2" data-testid="npc-dialogue-line">
              <p className="text-sm italic text-[#f1e0c5]/90">&ldquo;{currentLine}&rdquo;</p>
              {lines.length > 1 && (
                <button data-testid="npc-dialogue-reroll" onClick={reroll} className="text-[10px] uppercase text-[#D4AF37]/70 hover:text-[#D4AF37]">
                  Ask something else →
                </button>
              )}
            </div>
          ) : (
            <p className="text-xs text-[#f1e0c5]/40 italic">{npcToken.name} has nothing to say right now.</p>
          )}

          <div className="space-y-2">
            <p className="text-[10px] uppercase text-[#f1e0c5]/40 flex items-center gap-1.5"><Coins className="w-3 h-3" /> Give Money</p>
            <div className="flex gap-2">
              <Input
                data-testid="npc-give-money-amount"
                type="number"
                min={1}
                value={amount}
                onChange={(e) => setAmount(parseInt(e.target.value, 10) || 1)}
                className="bg-black/30 border-white/10 h-9"
              />
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger data-testid="npc-give-money-currency" className="w-20 bg-black/30 border-white/10 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cp">cp</SelectItem>
                  <SelectItem value="sp">sp</SelectItem>
                  <SelectItem value="gp">gp</SelectItem>
                  <SelectItem value="pp">pp</SelectItem>
                </SelectContent>
              </Select>
              <Button data-testid="npc-give-money-button" onClick={giveMoney} variant="secondary" className="rounded-full shrink-0">Give</Button>
            </div>
          </div>

          {npcToken.allow_attack && (
            <Button data-testid="npc-attack-button" onClick={attack} className="w-full rounded-full gap-2 bg-[#D90429] hover:bg-[#b8031f]">
              <Swords className="w-4 h-4" /> Attack {npcToken.name}
            </Button>
          )}

          {character && stats?.skills?.thievery && (
            <Button data-testid="npc-pickpocket-button" onClick={pickpocket} variant="secondary" className="w-full rounded-full gap-2">
              <Hand className="w-4 h-4" /> Attempt Pickpocket <span className="text-[#D4AF37]">({stats.skills.thievery.modifier >= 0 ? "+" : ""}{stats.skills.thievery.modifier})</span>
            </Button>
          )}

          {npcToken.is_shopkeeper && (
            <div className="space-y-2 border-t border-white/10 pt-3" data-testid="npc-shop-section">
              <p className="text-[10px] uppercase text-[#f1e0c5]/40 flex items-center gap-1.5"><Store className="w-3 h-3" /> Shop</p>
              {(npcToken.shop_stock || []).length === 0 && <p className="text-xs text-[#f1e0c5]/30 italic">Nothing for sale right now.</p>}
              <div className="space-y-2 max-h-64 overflow-y-auto scrollbar-thin">
                {(npcToken.shop_stock || []).map((item, idx) => (
                  <ItemCard
                    key={idx}
                    testId={`shop-item-${idx}`}
                    card={item}
                    actions={<Button data-testid={`buy-item-button-${idx}`} onClick={() => buyItem(idx, item)} size="sm" className="rounded-full text-xs bg-[#D90429] hover:bg-[#b8031f]">Buy for {item.price_amount || 0} {item.price_currency || "gp"}</Button>}
                  />
                ))}
              </div>
              {character?.inventory?.length > 0 && (
                <div className="pt-2">
                  <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1.5">Sell to {npcToken.name}</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto scrollbar-thin">
                    {character.inventory.map((item, idx) => (
                      <ItemCard
                        key={idx}
                        testId={`sell-item-${idx}`}
                        card={item}
                        actions={<Button data-testid={`sell-item-button-${idx}`} onClick={() => sellItem(idx, item)} size="sm" variant="secondary" className="rounded-full text-xs">Sell for {Math.max(1, Math.round((item.price_amount || item.price_gp || 0) / 2))} {item.price_currency || "gp"}</Button>}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
